import { DIRECTION_VECTORS, Direction, type EntityState } from '@game/protocol';

interface PendingInput {
  seq: number;
  /** Posição que o cliente previu **depois** de aplicar este input. */
  x: number;
  y: number;
}

/**
 * Alguma criatura viva ocupa (x, y, z), sem contar quem está perguntando?
 *
 * Espelha a segunda metade de `World.canOccupy` no servidor, que é uma pergunta só —
 * terreno **e** ocupação — e que não distingue quem ocupa: rato, banqueira e outro jogador
 * bloqueiam igual, porque o que o servidor consulta é um mapa de tile -> id de criatura
 * alimentado por toda entrada no mundo. Morto não está lá (a morte apaga a entrada no
 * mesmo tick, senão o cadáver trancaria o corredor onde ele acabou de cair) e item de chão
 * nunca esteve — ele vive noutra estrutura.
 *
 * Fica **fora** do `Predictor`, e de propósito: o `Predictor` continua recebendo um
 * predicado opaco e não sabe de onde veio a resposta. Quem conhece as duas fontes — o
 * terreno por chunk e as entidades em cena — é a `GameScene`, e é lá que a composição
 * acontece. Função de módulo, e não método, porque assim isto se testa sem instanciar
 * Phaser.
 *
 * As recusas abaixo são conservadoras por obrigação: a Área de Interesse do cliente é menor
 * que a região, então ele só conhece parte do mundo. Prever um passo que o servidor recusa
 * custa uma correção; inventar um bloqueio que o servidor não tem trava o jogador contra
 * uma parede que ninguém enxerga, e é o erro que não tem como ser diagnosticado.
 */
export function isOccupied(
  states: Iterable<EntityState>,
  x: number,
  y: number,
  z: number,
  selfId: number,
): boolean {
  for (const state of states) {
    // Mesmo `occupant === moverId` do servidor: o próprio corpo nunca é obstáculo.
    if (state.id === selfId) continue;
    // Vida não positiva é criatura que o servidor já tirou do mapa de ocupação; o Despawn
    // dela pode ainda estar em voo.
    if (state.health <= 0) continue;
    if (state.x === x && state.y === y && state.z === z) return true;
  }

  return false;
}

/**
 * O que a reconciliação encontrou.
 *
 * Os três existem porque **duas divergências muito diferentes eram a mesma** enquanto isto
 * era um booleano, e confundi-las fazia o personagem local piscar de tile em tile sempre que
 * alguma coisa que não o teclado o movia — a auto-caçada, notadamente, que anda o percurso
 * inteiro sem um único input do cliente.
 *
 * Previmos errado é um passo que **nunca aconteceu**: o desenho está adiante do mundo e a
 * única resposta honesta é o salto de volta. O servidor ter nos movido sozinho é o oposto —
 * o passo aconteceu de verdade, tem duração, e desenhá-lo como salto é jogar fora a
 * informação que o `stepMs` do Snapshot carrega justamente para animá-lo.
 *
 * Quem sabe distinguir um teleporte de um passo é o chamador, que tem a posição de antes e a
 * duração que veio no pacote; o que falta aqui é só dizer qual dos dois casos ocorreu.
 */
export const Correction = {
  /** Previsto e autoritativo coincidem. Nada a fazer. */
  None: 'none',
  /** Previmos um passo que o servidor não deu. O desenho volta de salto. */
  Mispredicted: 'mispredicted',
  /** O servidor nos moveu sem input nosso: automação, empurrão, teleporte. */
  External: 'external',
} as const;

export type Correction = (typeof Correction)[keyof typeof Correction];

/**
 * Client-side prediction com reconciliação (PRD §3.2).
 *
 * Sem predição, o personagem só sairia do lugar um RTT depois da tecla, o que é
 * perceptível mesmo em 60ms. O cliente então aplica o passo na hora e guarda o que
 * previu; quando o servidor confirma até um `seq`, o previsto para aquele `seq` é
 * comparado com o autoritativo. Divergiu, o servidor vence — sempre.
 */
export class Predictor {
  x = 0;
  y = 0;
  direction: Direction = Direction.South;

  /** Trava de caminhada local, espelhando a do servidor. */
  cooldownMs = 0;

  /**
   * Duração do passo, **ditada pelo servidor**: vem no Welcome e é atualizada a cada
   * passo confirmado. O zero inicial nunca é usado — a cena só processa input depois
   * de ter recebido o Welcome.
   *
   * Não existe valor padrão aqui de propósito. Um número chutado neste campo é
   * silencioso quando erra: se o cliente supõe um passo mais longo que o real, ele
   * simplesmente anda devagar, e nada no jogo acusa que a culpa é do cliente.
   */
  stepDurationMs = 0;

  private pending: PendingInput[] = [];

  reset(x: number, y: number): void {
    this.x = x;
    this.y = y;
    this.cooldownMs = 0;
    this.pending.length = 0;
  }

  get canStep(): boolean {
    return this.cooldownMs <= 0;
  }

  update(dtMs: number): void {
    if (this.cooldownMs > 0) this.cooldownMs = Math.max(0, this.cooldownMs - dtMs);
  }

  /**
   * O mundo que o cliente **tem** permite este passo?
   *
   * `isFree` responde por terreno e ocupação de uma vez, como `World.canOccupy` no
   * servidor; quem compõe as duas metades é a cena (ver `isOccupied` acima e
   * `GameScene.pollInput`). Não é uma segunda autoridade: serve só para o cliente não
   * prever um passo que já sabe impossível e ter que corrigir logo em seguida. A recusa
   * que conta continua sendo a do servidor.
   *
   * Fica separada de `applyLocalStep` porque quem chama precisa da resposta **antes** de
   * decidir se manda o frame de Move — ver `GameScene.pollInput`.
   */
  canWalk(direction: Direction, isFree: (x: number, y: number) => boolean): boolean {
    const vector = DIRECTION_VECTORS[direction];
    return isFree(this.x + vector.dx, this.y + vector.dy);
  }

  /**
   * Aplica o passo localmente, se o mundo local permitir.
   *
   * **O cooldown é armado nos dois casos**, e é a assimetria que faltava aqui que
   * derrubava jogador honesto (PRD §11.1, item 1): sem travar na recusa, `canStep`
   * continuava verdadeiro, `take()` devolvia a mesma direção enquanto a tecla estivesse
   * presa, e saía um Move por **quadro renderizado** — 60 a 140 por segundo, contra os 7
   * de uma caminhada livre e contra o teto de 40/s do gateway. Andar de frente para uma
   * parede é o gesto mais comum do gênero, e era indistinguível de um cliente adulterado.
   */
  applyLocalStep(
    direction: Direction,
    seq: number,
    isFree: (x: number, y: number) => boolean,
  ): boolean {
    this.direction = direction;

    const vector = DIRECTION_VECTORS[direction];
    const targetX = this.x + vector.dx;
    const targetY = this.y + vector.dy;

    if (!isFree(targetX, targetY)) {
      // Só virou. Não há passo a prever, então nada entra em `pending` — mas a trava vale
      // igual, porque o custo de tentar já foi pago.
      this.cooldownMs = this.stepDurationMs;
      return false;
    }

    this.x = targetX;
    this.y = targetY;
    this.cooldownMs = this.stepDurationMs;
    this.pending.push({ seq, x: targetX, y: targetY });
    return true;
  }

  /**
   * Confronta a posição autoritativa com o que havíamos previsto para o mesmo input.
   *
   * Ver `Correction` para por que a resposta distingue "previmos errado" de "fomos movidos
   * sem input nosso": os dois mexem na posição, e só o primeiro é um salto.
   */
  reconcile(serverX: number, serverY: number, ackSeq: number): Correction {
    const acked = this.pending.find((input) => input.seq === ackSeq);
    const mispredicted =
      acked !== undefined && (acked.x !== serverX || acked.y !== serverY);

    // Tudo até `ackSeq` já está embutido na posição que o servidor mandou.
    this.pending = this.pending.filter((input) => input.seq > ackSeq);

    if (mispredicted) {
      // Previmos errado: descartamos os inputs em voo, porque foram construídos
      // sobre uma posição que nunca existiu no servidor.
      this.pending.length = 0;
      this.x = serverX;
      this.y = serverY;
      // **A trava de caminhada não é devolvida.** Ela era zerada aqui enquanto a
      // reconciliação só rodava quando o servidor de fato mexia na entidade local; agora
      // que ela roda a cada Snapshot, o passo *recusado* também chega aqui — e devolver a
      // trava nesse caso reabre o defeito do §11.1, item 1 por outro caminho: com a tecla
      // segurada contra algo que o cliente não tem como prever (uma imobilização, que não
      // viaja no protocolo), o ciclo prever→corrigir fecha em um RTT em vez de em um passo,
      // o personagem estrobosca e sai um Move por quadro. O custo de tentar já foi pago em
      // `applyLocalStep`, e uma correção não o desfaz.
      return Correction.Mispredicted;
    }

    // Sem nada em voo, previsto e autoritativo têm de coincidir. Se não coincidem,
    // algo nos moveu sem input nosso (automação, empurrão, teleporte) e o servidor manda.
    if (this.pending.length === 0 && (this.x !== serverX || this.y !== serverY)) {
      this.x = serverX;
      this.y = serverY;
      return Correction.External;
    }

    return Correction.None;
  }
}
